/**
 * AuthEngineTypes
 */

/**
 * User metadata refers to user-specific attributes (e.g., zip codes, parental ratings, user IDs, etc.) that are maintained by MVPDs and provided to Programmers through the Adobe Pass Authentication REST API V2.
 *
 * {@link https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/entitlements/user-metadata}
 *
 * @privateRemarks
 * Adobe AccessEnabler metadata keys, except for `DEVICE_ID`,
 * `AUTHENTICATED_TTL` and `AUTHORIZED_TTL` -- these 3 are special values that
 * are handled differently per platform by the AccessEnabler API.
 */
export enum MetadataKey {
  /**
   * Account identifier.
   *
   * The attribute value can be a household identifier or a sub-account identifier. The userID value will differ from the householdID if the MVPD supports sub-accounts and the current user is not the primary account holder.
   */
  USER_ID = 'userID',

  /**
   * User's zip code.
   *
   * The attribute value can be used to deliver localized news, weather updates or sporting events. The zip value represents sensitive data that needs legal agreements with the MVPD. When encrypted, the representation of the zip key will be a String instead of an Array.
   */
  ZIP = 'zip',

  /**
   * User's encrypted zip code.
   *
   * The attribute is specific to Comcast.
   */
  ENCRYPTED_ZIP = 'encryptedZip',

  /**
   * Maximum parental rating for the current user.
   *
   * The attribute value can be used to filter content that is not suited for current user based on "MPAA" or "VCHIP" ratings.
   */
  MAX_RATING = 'maxRating',

  /**
   * Account identifier for parental control.
   *
   * The attribute value can be used to differentiate between household and sub-account usage. Sometimes it can be used as a parental control substitute if true ratings are not available, if the user was logged in with the household account, they can watch, otherwise, rated content would not be displayed. There is a lot of variation across MVPDs for how this is represented (e.g., household user ID, head of household ID, head of household flag, etc.), if the MVPD does not support sub-accounts, this will be identical to userID.
   */
  HOUSEHOLD_ID = 'householdID',

  /**
   * List of channels the user is entitled to view.
   *
   * The attribute value can be used to filter various channels from portals that aggregate multiple networks. Our recommendation is to use the Preauthorize API instead of this user metadata attribute to filter out channels that are not available to the user.
   */
  CHANNEL_ID = 'channelID',

  /**
   * Attribute that indicates if current user is head of household or not.
   *
   * The attribute is specific to Synacor.
   */
  HEAD_OF_HOUSEHOLD = 'is_hoh',

  /**
   * Attribute that indicates if current user is a primary or secondary account holder.
   *
   * The attribute is specific to AT&T. The primaryOID value is the same as the userID value when the typeID value is set to "Primary".
   */
  TYPE_ID = 'typeID',

  /**
   * Account identifier.
   *
   * The attribute is specific to AT&T. The primaryOID value is the same as the userID value when the typeID value is set to "Primary".
   */
  PRIMARY_OID = 'primaryOID',

  /**
   * Language settings.
   *
   * The attribute value can be used to display messages in accordance to the user's language preferences.
   */
  LANGUAGE = 'language',

  /**
   * Account identifier for concurrency monitoring.
   *
   * The attribute value can be used to enforce concurrency limits across MVPD and Programmer sites and apps. The upstreamUserID value is the same as the userID value for most MVPDs.
   */
  UPSTREAM_USER_ID = 'upstreamUserID',

  /**
   * Attribute that indicates if current user authenticated through HBA or not.
   */
  HBA_STATUS = 'hba_status',

  ON_NET = 'onNet',
  IN_HOME = 'inHome',

  /**
   * Attribute that indicates if current device can mirror screen or not.
   *
   * The attribute is specific to Spectrum.
   */
  ALLOW_MIRRORING = 'allowMirroring',

  // The following are not Adobe keys yet MUST match the keys defined in iOS & Android for Nexus:
  DEVICE_ID = 'DEVICE_ID',
  AUTHENTICATED_TTL = 'AUTHENTICATED_TTL',
  AUTHORIZED_TTL = 'AUTHORIZED_TTL',
}

/**
 * An optional dictionary of arguments provided to `fetchMetadata`
 *
 * @remarks
 * Only used when requesting authZ TTL because the resource ID is required
 */
export type MetadataArgsMap = Record<string, string>

/** {@link MetadataValue} response to requests for `DEVICE_ID`, `AUTHENTICATED_TTL`, `AUTHORIZED_TTL` */
export type SimpleMetadataValue = string | null

/** {@link MetadataValue} response to requests for user metadata */
export type UserMetadataValue = string | boolean | number | Record<string, unknown> | null

/**
 * According to Adobe docs, the response metadata can be either "simple" (or
 * "static"), which is a string (or null), or "user metadata", which "can be
 * a primitive or JSON object"
 *
 * Source: http://tve.helpdocsonline.com/javascript-api-reference-v2$setMetaData
 */
export type MetadataValue = SimpleMetadataValue | UserMetadataValue
